import { AxiosError, AxiosResponse } from "axios";
import { AttachmentLightbox } from "components/AttachmentLightbox/AttachmentLightbox";
import { AttachmentListItem } from "components/AttachmentListItem";
import { CaseActionCommentSection } from "components/CaseManagement/Comments/CaseActionCommentSection";
import { DropZone } from "components/CustomUpload/ImageDropZone";
import { DatePicker } from "components/DatePicker";
import { MultilineTextField } from "components/MultilineTextField";
import { ActionInfo } from "components/MyActions/ActionInfo";
import ProgressIndicatorContainer from "components/ProgressIndicatorContainer";
import { API_URL_CASE_ACTIONS } from "constants/api_urls";
import { AxiosContext } from "contexts/AxiosContext";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { createUniqueFilename } from "core/utils/files";
import { useFormik } from "formik";
import { useGetAttachmentURI } from "hooks/case-action/useGetAttachmentURI";
import { IApiError } from "interfaces/api-error";
import { AttachmentDetail, OriginalFile } from "interfaces/attachment";
import { CaseAction, CaseActionStatusType } from "interfaces/case-action";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material/styles";
import * as yup from "yup";

import { Box, Button, Chip, Paper, Typography } from "@mui/material";
import List from "@mui/material/List";
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { formatISO, parseISO } from "date-fns";
import { downloadAsDataUri } from "core/utils/aws/downloadAsDataUri";
import { useUnmount } from "react-use";
import { downloadFile } from "core/utils/aws/downloadFile";
import { getAttachment } from "core/utils/aws/getAttachment";

type FileObject = {
  file: File;
  data: string;
};

interface CaseActionFileItem {
  id?: number | null | undefined;
  caseActionId?: number | null | undefined;
  file: File | null;
  original_file: OriginalFile;
}

interface SubmissionAttachmentFile {
  id?: number | null | undefined;
  caseActionId?: number | null | undefined;
  file: File | null;
  original_file: OriginalFile;
}

interface SubmitYourAction {
  scope_of_work: string;
  estimated_completion_date: string | null;
}

const validationSchema = yup.object({
  work_scope: yup
    .string()
    .required("Summary of Work Scope is a required field")
    .nullable()
    .trim(),
  estimated_completion_date: yup
    .date()
    .label("Estimated completion date")
    .nullable()
});

const isActionOpen = (
  actionStatus: CaseActionStatusType | undefined
): boolean => {
  return actionStatus !== undefined
    ? [
        CaseActionStatusType.Open,
        CaseActionStatusType.InProgress,
        CaseActionStatusType.InReview
      ].includes(actionStatus)
    : false;
};

export interface ActionPanelProps {
  selectedActionId: number | null;
  onFormChange: (hasChanges: boolean) => void;
}

const initialFormValues = {
  actionId: -1,
  work_scope: "",
  estimated_completion_date: null
};

const removeSubmittedAttachmentFromCache = (
  queryClient: QueryClient,
  selectedActionId: number,
  attachmentToDeleteId: number
): void => {
  queryClient.setQueryData(
    ["case-action-attachments", selectedActionId],
    (cachedAttachments: AttachmentDetail[] | undefined) => {
      return cachedAttachments
        ?.filter(attachment => attachment.id !== attachmentToDeleteId)
        .sort((a, b) =>
          a.original_file.name.localeCompare(b.original_file.name, "en", {
            sensitivity: "base"
          })
        );
    }
  );
};

export const ActionPanel = ({
  selectedActionId,
  onFormChange
}: ActionPanelProps): JSX.Element => {
  const controller = new AbortController();
  useUnmount(() => {
    controller.abort();
  });

  const [expandedImageUrl, setExpandedImageUrl] = useState<string | null>(null);
  const attachmentUri = useGetAttachmentURI(expandedImageUrl);
  const [fileItems, setFileItems] = useState<CaseActionFileItem[]>([]);
  const [attachmentIdsToDelete, setAttachmentIdsToDelete] = useState<number[]>(
    []
  );

  const { axios } = useContext(AxiosContext)!;
  const queryClient = useQueryClient();
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);

  useEffect(() => {
    setFileItems([]);
    setAttachmentIdsToDelete([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedActionId]);

  const handleAxiosError = (defaultErrorMessage: string) => (
    err: AxiosError<IApiError>
  ) => {
    addSnackbarToQueue({
      severity: "error",
      message: err?.response?.data?.error || defaultErrorMessage
    });
  };

  const submitAction = useMutation(["submit-your-action"], {
    mutationFn: (actionSubmission: SubmitYourAction) =>
      axios
        .post<SubmitYourAction, AxiosResponse<SubmitYourAction>>(
          `${API_URL_CASE_ACTIONS}/${selectedActionId}/submit`,
          actionSubmission
        )
        .then(r => r.data)
  });

  const uploadSubmissionFile = useMutation(["upload-submission-file"], {
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return axios
        .post<any, AxiosResponse<AttachmentDetail>>(
          `${API_URL_CASE_ACTIONS}/${selectedActionId}/submitted_attachments`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data"
            }
          }
        )
        .then(r => r);
    },
    onError: handleAxiosError("Error uploading file.")
  });

  const deleteSubmissionFile = useMutation(
    ["delete__submitted_attachments", "detach"],
    {
      mutationFn: (fileId: number) => {
        return axios
          .post(
            `${API_URL_CASE_ACTIONS}/${selectedActionId}/submitted_attachments/detach/${fileId}`
          )
          .then(r => r);
      },
      onError: handleAxiosError("Error deleting file.")
    }
  );

  const formik = useFormik<{
    actionId: number;
    work_scope: string;
    estimated_completion_date: Date | null;
  }>({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async values => {
      if (!formik.isValid) return;

      formik.setSubmitting(true);

      const successfullyDeletedAttachmentIds: number[] = [];
      const successfullyUploadedAttachmentIds: number[] = [];

      const deletePromises = attachmentIdsToDelete
        .filter(
          aid =>
            // Ensure we are only deleting submitted attachments which belong to selected case.
            aid &&
            caseActionSubmittedAttachmentsQuery.data?.some(sa => sa.id === aid)
        )
        .map(delete_attachment_id => {
          return deleteSubmissionFile
            .mutateAsync(delete_attachment_id)
            .then(() => {
              if (selectedActionId) {
                successfullyDeletedAttachmentIds.push(delete_attachment_id);
                removeSubmittedAttachmentFromCache(
                  queryClient,
                  selectedActionId,
                  delete_attachment_id
                );
              }
            })
            .catch(err => {
              console.info("err", err);
              throw new Error("Failed to delete file.");
            });
        });

      const uploadPromises = fileItems
        // Ensure we are only uploading files which belong to selected case.
        .filter(fi => fi.caseActionId === selectedActionId)
        .map(
          f =>
            f.file &&
            uploadSubmissionFile
              .mutateAsync(f.file)
              .then(response => {
                if (selectedActionId && f.file) {
                  successfullyUploadedAttachmentIds.push(response.data.id);
                }
                return response;
              })
              .catch(err => {
                console.info("err", err);
                throw new Error(`Failed to upload file ${f.file?.name}.`);
              })
              .finally(() => {
                caseActionSubmittedAttachmentsQuery.refetch();
              })
        );

      try {
        await Promise.all(deletePromises);
        await Promise.all(uploadPromises);
        // Clear out the local attachment IDs to delete because they will have been deleted now.
        setAttachmentIdsToDelete([]);
        // Clear out the local unsaved files because they will have been saved now.
        setFileItems([]);
        await submitAction.mutateAsync({
          scope_of_work: values.work_scope,
          estimated_completion_date: values.estimated_completion_date
            ? formatISO(values.estimated_completion_date, {
                representation: "date"
              })
            : null
        });
        addSnackbarToQueue({
          severity: "success",
          message: "Action submitted successfully."
        });
        // Refetch the case action and the action list
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["case-action"] }),
          queryClient.invalidateQueries({ queryKey: ["my-actions"] })
        ]);
        formik.resetForm({ values: values });
      } catch (err) {
        console.info(err);

        if (successfullyDeletedAttachmentIds.length !== deletePromises.length) {
          addSnackbarToQueue({
            severity: "error",
            message: "Failed to delete one or more files."
          });
        }
        if (
          successfullyUploadedAttachmentIds.length !== uploadPromises.length
        ) {
          addSnackbarToQueue({
            severity: "error",
            message: "Failed to upload one or more files."
          });
        }
        addSnackbarToQueue({
          severity: "error",
          message: "Failed to submit."
        });
      } finally {
        formik.setSubmitting(false);
      }
    }
  });

  const fetchCaseAction = useQuery({
    queryKey: ["case-action", selectedActionId],
    queryFn: () =>
      axios
        .get<CaseAction>(`${API_URL_CASE_ACTIONS}/${selectedActionId}`)
        .then(r => r.data),
    onSuccess: data => {
      // We only want to set the form if it hasn't been touched
      // This is because we don't want to override any values the user enters
      if (selectedActionId !== formik.values.actionId || !formik.dirty) {
        const newValues = {
          actionId: selectedActionId || -1,
          work_scope: data?.scope_of_work ?? "",
          estimated_completion_date: data?.estimated_completion_date
            ? parseISO(data.estimated_completion_date)
            : null
        };
        formik.resetForm({ values: newValues });
      }
    },
    onError: handleAxiosError("Error fetching Case Action."),
    cacheTime: 0,
    enabled: selectedActionId !== null
  });

  const caseActionSubmittedAttachmentsQuery = useQuery({
    queryKey: ["case-action-attachments", selectedActionId],
    queryFn: async () => {
      const { data } = await axios.get<AttachmentDetail[]>(
        `${API_URL_CASE_ACTIONS}/${selectedActionId}/submitted_attachments`
      );

      return data;
    },
    cacheTime: Infinity,
    staleTime: Infinity,
    enabled: selectedActionId !== null
  });

  const selectedAction = fetchCaseAction?.data;
  const loading =
    fetchCaseAction.isLoading ||
    uploadSubmissionFile.isLoading ||
    deleteSubmissionFile.isLoading;

  const theActionIsOpen = isActionOpen(selectedAction?.action_status);

  const newlySubmittedAttachments = useMemo<SubmissionAttachmentFile[]>(() => {
    return fileItems
      .filter(fi => fi.caseActionId === selectedActionId)
      .map<SubmissionAttachmentFile>(fi => ({
        caseActionId: selectedActionId,
        fileName: fi.file?.name,
        file: fi.file,
        original_file: {
          name: fi.file?.name ?? "",
          download_headers: {} as any,
          type: fi.file?.type ?? "",
          url: ""
        }
      }));
  }, [fileItems, selectedActionId]);

  useEffect(() => {
    // We want to notify the parent if there are any changes that aren't currently saved.
    onFormChange(
      newlySubmittedAttachments.length > 0 ||
        attachmentIdsToDelete.length > 0 ||
        formik.dirty
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.dirty, newlySubmittedAttachments, attachmentIdsToDelete]);

  const existingSubmittedAttachments = useMemo<
    SubmissionAttachmentFile[]
  >(() => {
    return (caseActionSubmittedAttachmentsQuery?.data ?? [])
      .filter(attachment => !attachmentIdsToDelete.includes(attachment.id))
      .map<SubmissionAttachmentFile>(attachment => ({
        id: attachment.id,
        caseActionId: selectedActionId,
        fileName: attachment.original_file.name,
        file: null,
        downloadUrl: attachment.original_file.url,
        original_file: attachment.original_file
      }));
  }, [
    attachmentIdsToDelete,
    caseActionSubmittedAttachmentsQuery?.data,
    selectedActionId
  ]);

  const handleFileAddedEvent = useCallback(
    async (fileAddedEvtList: FileObject[]) => {
      if (fileAddedEvtList.length === 0) {
        return;
      }

      const existingFilenames = existingSubmittedAttachments.map(
        esa => esa.original_file.name
      );

      const newItems: CaseActionFileItem[] = fileAddedEvtList.map(
        fileAddedEvt => {
          let fileName = createUniqueFilename(fileAddedEvt.file.name, [
            ...existingFilenames,
            ...fileItems.map(fi => fi.original_file.name)
          ]);

          let newFile = new File([fileAddedEvt.file], fileName, {
            type: fileAddedEvt.file.type,
            lastModified: fileAddedEvt.file.lastModified
          });

          return {
            caseActionId: selectedActionId,
            file: newFile,
            original_file: {
              name: fileName,
              type: fileAddedEvt.file.type,
              url: "",
              download_headers: {} as any
            }
          };
        }
      );

      if (selectedActionId) {
        setFileItems([...fileItems, ...newItems]);
      }
    },
    [existingSubmittedAttachments, fileItems, selectedActionId]
  );

  return (
    <ActionContainer>
      <PaddedPaper>
        <Box component="section">
          <Typography variant="h5" component="h1">
            Action Info
          </Typography>
          <ProgressIndicatorContainer
            loading={loading}
            error={fetchCaseAction?.isError}
          >
            <ActionInfo selectedAction={fetchCaseAction?.data} />
          </ProgressIndicatorContainer>
        </Box>
      </PaddedPaper>

      <RightColumnBottomSectionContainer>
        <PaddedPaper sx={{ flex: 1, minWidth: 0 }}>
          <TitleWrapper>
            <Typography variant="h5" component="h1">
              Your Action
            </Typography>
            {!loading && (
              <Chip
                data-qa="MA-Your-Action-Status"
                size="small"
                label={
                  <ChipLabel>
                    <ChipTitle>Status:</ChipTitle>
                    {selectedAction?.action_status_detail?.name ?? "-"}
                  </ChipLabel>
                }
              />
            )}
          </TitleWrapper>
          <ProgressIndicatorContainer
            loading={fetchCaseAction?.isLoading}
            error={fetchCaseAction?.isError}
          >
            <MySubmissionContainer>
              <MultilineTextField
                variant="outlined"
                name="work_scope"
                label="Summary of Work Scope"
                data-qa="MA-Text-Work-Scope"
                size="small"
                placeholder="Enter text here"
                value={formik.values.work_scope}
                required
                InputLabelProps={{
                  shrink: true
                }}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={!theActionIsOpen}
                error={
                  formik.touched.work_scope && Boolean(formik.errors.work_scope)
                }
                helperText={
                  formik.touched.work_scope && formik.errors.work_scope
                }
              />
              <DatePicker
                date={formik.values.estimated_completion_date}
                onChange={(ev: string | number | Date) =>
                  formik.setFieldValue("estimated_completion_date", ev)
                }
                slotProps={{
                  textField: {
                    InputLabelProps: {
                      shrink: true
                    },
                    helperText:
                      formik.touched.estimated_completion_date &&
                      formik.errors.estimated_completion_date,
                    error:
                      formik.touched.estimated_completion_date &&
                      Boolean(formik.errors.estimated_completion_date)
                  }
                }}
                label="Estimated Completion Date"
                data-qa="MA-Text-Estimated-Completion-Date"
                ariaLabel="nca-form-expected-due-date-picker"
                clearable
                disableFuture={false}
                fullWidth
                disabled={!theActionIsOpen}
              />
              {theActionIsOpen && (
                <div
                  role="button"
                  aria-label="Drop Zone Container"
                  data-qa="MA-Submission-Attachment-Drop-Zone"
                >
                  <DropZone setFileUploaded={handleFileAddedEvent} />
                </div>
              )}
              {!formik.isSubmitting ? (
                <StyledFileList
                  data-qa="MA-Submission-Attachment-List"
                  role="list"
                  aria-label="Submission Attachment List"
                >
                  {[
                    ...newlySubmittedAttachments,
                    ...existingSubmittedAttachments
                  ].map((attachment, index) => (
                    <AttachmentListItem
                      key={index}
                      filename={attachment.original_file.name}
                      showDownload={!!attachment.id}
                      showExpand={!!attachment.id}
                      onExpandImage={async (): Promise<void> => {
                        try {
                          const refetchedData = await getAttachment(
                            axios,
                            attachment.id
                          );

                          let {
                            url,
                            download_headers: headers
                          } = refetchedData.original_file;

                          if (refetchedData.thumbnail_large) {
                            url = refetchedData.thumbnail_large.url;
                            headers = refetchedData.thumbnail_large!
                              .download_headers;
                          }

                          const image = await downloadAsDataUri({
                            url,
                            headers,
                            signal: controller.signal
                          });

                          setExpandedImageUrl(image);
                        } catch (err) {
                          console.info(err);
                        }
                      }}
                      onDownloadFile={async (): Promise<void> => {
                        try {
                          const refetchedData = await getAttachment(
                            axios,
                            attachment.id
                          );

                          let {
                            url,
                            download_headers: headers,
                            name: filename
                          } = refetchedData.original_file;

                          if (refetchedData.thumbnail_large) {
                            url = refetchedData.thumbnail_large.url;
                            headers = refetchedData.thumbnail_large!
                              .download_headers;
                          }

                          await downloadFile({
                            url,
                            headers,
                            filename,
                            signal: controller.signal
                          });
                        } catch (err) {
                          console.info(err);
                        }
                      }}
                      onDelete={
                        theActionIsOpen
                          ? () => {
                              if (attachment.id) {
                                // If the file has an ID it means it exists in the backend so we need to add
                                // it to the list of attachments to be deleted.  We will call the API to delete
                                // these files when the user clicks on the submit button.
                                setAttachmentIdsToDelete([
                                  ...attachmentIdsToDelete.filter(
                                    attachmentId =>
                                      attachmentId !== attachment.id
                                  ),
                                  attachment.id
                                ]);
                              } else {
                                // If the file doesn't have an ID that means it hasn't been saved yet and we
                                // just need to remove the file from local state.
                                setFileItems(
                                  fileItems.filter(
                                    file =>
                                      file.original_file.name !==
                                      attachment.original_file.name
                                  )
                                );
                              }
                            }
                          : undefined
                      }
                    />
                  ))}
                </StyledFileList>
              ) : (
                <ProgressIndicatorContainer
                  loading={formik.isSubmitting}
                  error={true}
                />
              )}

              {attachmentUri && (
                <AttachmentLightbox
                  attachmentUrl={attachmentUri}
                  onClose={() => setExpandedImageUrl(null)}
                />
              )}

              {theActionIsOpen && (
                <StyledSubmitButton
                  disabled={
                    loading ||
                    !formik.isValid ||
                    !(
                      formik.dirty ||
                      newlySubmittedAttachments.length > 0 ||
                      attachmentIdsToDelete.length > 0
                    ) ||
                    formik.isSubmitting
                  }
                  onClick={() => {
                    formik.handleSubmit();
                  }}
                  variant="contained"
                  color="primary"
                  data-qa="MA-Button-Submit-Changes"
                >
                  {fetchCaseAction?.data?.action_status ===
                  CaseActionStatusType.InReview
                    ? "Submit changes"
                    : "Submit"}
                </StyledSubmitButton>
              )}
            </MySubmissionContainer>
          </ProgressIndicatorContainer>
        </PaddedPaper>

        <PaddedPaper sx={{ flex: 1 }}>
          <ProgressIndicatorContainer
            loading={loading}
            error={fetchCaseAction?.isError}
          >
            {selectedAction && (
              <CaseActionCommentSection actionId={selectedAction.id} />
            )}
          </ProgressIndicatorContainer>
        </PaddedPaper>
      </RightColumnBottomSectionContainer>
    </ActionContainer>
  );
};

const ActionContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  overflow-y: auto;
`;
ActionContainer.displayName = "ActionContainer";

const PaddedPaper = styled(Paper)`
  box-sizing: border-box;
  padding: ${({ theme }) => theme.spacing(2)};
`;
PaddedPaper.displayName = "PaddedPaper";

const TitleWrapper = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
TitleWrapper.displayName = "TitleWrapper";

const ChipLabel = styled("div")`
  display: flex;
`;
ChipLabel.displayName = "ChipLabel";

const ChipTitle = styled("div")`
  font-weight: bold;
  padding-right: ${({ theme }) => theme.spacing(0.5)};
`;
ChipTitle.displayName = "ChipTitle";

const RightColumnBottomSectionContainer = styled("div")`
  display: flex;
  flex: 1;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing(2)};

  @media only screen and (max-width: 1280px) {
    flex-direction: column;
  }
`;
RightColumnBottomSectionContainer.displayName =
  "RightColumnBottomSectionContainer";

const MySubmissionContainer = styled("div")`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  padding-top: ${({ theme }) => theme.spacing(2)};
  padding-bottom: ${({ theme }) => theme.spacing(2)};
  & > div:not(.MuiFormControl-root) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    background-color: #d9d9d9;
  }
`;
MySubmissionContainer.displayName = "MySubmissionContainer";

const StyledFileList = styled(List)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: 0;
`;
StyledFileList.displayName = "StyledFileList";

const StyledSubmitButton = styled(Button)`
  display: block;
  margin-left: auto;
`;
StyledSubmitButton.displayName = "StyledSubmitButton";
